/**
 * La marca de un informe DEL CHAT con la cabecera que pidió la persona (F4 de
 * «Personalizar informes», R10, 29-09-2026): «ponme nuestro NIF y dirección en la
 * cabecera».
 *
 * El informe del asistente trae `cabecera: {campos, quitar}` con NOMBRES de campo
 * (M4: ni el chat ni `context.docs` guardan valores). Aquí se ponen los valores:
 *
 *  - admin y encargado leen la fila de `company_report_branding` (RLS) y la
 *    cabecera lleva lo que la empresa enseña siempre + lo pedido − lo quitado;
 *  - cualquier otro (o si la fila no se puede leer): solo lo que la empresa enseña
 *    (lo que ya da la RPC `marca_informes`), menos lo quitado. Lo oculto nunca le
 *    llega (M3).
 *
 * Como `cargarMarca`: NUNCA lanza y espera como mucho `ESPERA_MS`; sin marca (o sin
 * interruptor), `null` y el informe sale como siempre (M2).
 */
import { supabase } from "@/integrations/supabase/client";
import i18n from "@/i18n/config";
import { cargarMarca, ESPERA_MS, estadoMarca } from "./cargarMarca";
import { componerLineas, soloVisibles, type CamposCabecera, type EtiquetasCabecera } from "./lineas";
import { cabeceraPedida, camposDeLaCabecera, CAMPOS_CABECERA, PARTES_DIRECCION, type CabeceraPedida } from "./reglasCabecera";
import type { MarcaInformes } from "./tipos";

export type FilaCabecera = CamposCabecera & { visibles?: string[] | null };

const lleno = (v: unknown) => typeof v === "string" && v.trim() !== "";

/**
 * Las líneas de la cabecera de un informe del chat. PURA.
 *  - `fila`: la fila entera (admin o encargado) o null.
 *  - `publicos`: lo que da la RPC (ya solo lo que la empresa enseña).
 */
export function lineasConCabecera(o: {
  fila: FilaCabecera | null;
  publicos: CamposCabecera | null;
  pedida: CabeceraPedida | null;
  et: EtiquetasCabecera;
  nombre: string;
}): string[] {
  if (o.fila) {
    return componerLineas(soloVisibles(o.fila, camposDeLaCabecera(o.fila.visibles ?? [], o.pedida)), o.et, o.nombre);
  }
  const pub = o.publicos ?? {};
  const ensena = CAMPOS_CABECERA.filter((c) =>
    c === "direccion" ? ["direccion", ...PARTES_DIRECCION].some((k) => lleno(pub[k as keyof CamposCabecera])) : lleno(pub[c]),
  );
  return componerLineas(soloVisibles(pub, camposDeLaCabecera(ensena, { campos: [], quitar: o.pedida?.quitar })), o.et, o.nombre);
}

/** Lo pedido en una clave corta, para las claves de caché de los exportes. PURA. */
export function claveCabecera(raw: unknown): string {
  const p = cabeceraPedida(raw);
  if (!p) return "";
  return `c:${p.campos.join(",")}${p.quitar?.length ? `|q:${p.quitar.join(",")}` : ""}`;
}

async function leerFila(companyId: string, esperaMs: number): Promise<FilaCabecera | null> {
  try {
    const c = new AbortController();
    const t = setTimeout(() => c.abort(), esperaMs);
    const { data, error } = await supabase
      .from("company_report_branding" as never)
      .select(["visibles", ...CAMPOS_CABECERA, ...PARTES_DIRECCION].join(", "))
      .eq("company_id" as never, companyId as never)
      .abortSignal(c.signal)
      .maybeSingle();
    clearTimeout(t);
    if (error || !data) return null;
    return data as unknown as FilaCabecera;
  } catch {
    return null;
  }
}

/**
 * La marca de un informe del chat, con su cabecera. Sin `cabecera` (o vacía) es
 * `cargarMarca` tal cual. Nunca lanza.
 */
export async function cargarMarcaInforme(
  companyId: string | null | undefined,
  cabecera: unknown,
  esperaMs = ESPERA_MS,
): Promise<MarcaInformes | null> {
  const pedida = cabeceraPedida(cabecera);
  if (!pedida) return cargarMarca(companyId, esperaMs);
  if (!companyId) return null;
  try {
    const [base, estado, fila] = await Promise.all([cargarMarca(companyId, esperaMs), estadoMarca(companyId), leerFila(companyId, esperaMs)]);
    if (!base) return null;
    const et = {
      nif: i18n.t("marcaInformes.pdf.nif", { defaultValue: "NIF" }),
      tel: i18n.t("marcaInformes.pdf.tel", { defaultValue: "Tel." }),
    };
    return {
      ...base,
      lineas: lineasConCabecera({ fila, publicos: estado.datos, pedida, et, nombre: base.nombre }),
      // Otra cabecera es otro documento: la clave de caché lo tiene que notar.
      version: `${base.version}|${claveCabecera(pedida)}|${fila ? "fila" : "rpc"}`,
    };
  } catch {
    return null;
  }
}
